import { FC, useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Stack,
  Link,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NextLink from "next/link";
import { HeaderNavInterface, SocialGroupInterface } from "src/lib/utils/interfaces";
import { SalifySocialIcon } from "src/lib/components/ui/icon/social-icon";

interface HaiAnhResponsiveHeaderNavProps {
  nav: Array<HeaderNavInterface>;
  socialGroup: Array<SocialGroupInterface> | undefined;
  hotline: string | undefined;
  isActive: (item: HeaderNavInterface, sectionId: string | undefined) => boolean;
  onClick: (item: HeaderNavInterface, sectionId: string | undefined) => void;
}

export const HaiAnhResponsiveHeaderNav: FC<HaiAnhResponsiveHeaderNavProps> = ({
  nav,
  socialGroup,
  hotline,
  isActive,
  onClick,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (item: HeaderNavInterface) => {
    const sectionId = item.target?.replace("#", "");
    onClick(item, sectionId);
    setMobileOpen(false); // Close drawer after navigation
  };

  const drawer = (
    <Box sx={{ width: 280, height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: 2,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#FFC107" }}>
          Menu
        </Typography>
        <IconButton onClick={handleDrawerToggle} sx={{ color: "#333" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={{ paddingTop: 1 }}>
        {nav.map((item) => {
          const sectionId = item.target?.replace("#", "");
          const active = isActive(item, sectionId);

          return (
            <ListItem key={item.label} disablePadding>
              {item.action === "link" ? (
                <NextLink
                  href={item.target || "#"}
                  style={{ width: "100%", textDecoration: "none", color: "inherit" }}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemButton
                    sx={{
                      paddingY: 1.5,
                      paddingX: 2,
                      "&:hover": {
                        backgroundColor: "rgba(255, 193, 7, 0.08)",
                      },
                      ...(active && {
                        backgroundColor: "rgba(255, 193, 7, 0.12)",
                        "& .MuiListItemText-primary": {
                          color: "#FFC107",
                          fontWeight: 600,
                        },
                      }),
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: "16px",
                        fontWeight: active ? 600 : 500,
                      }}
                    />
                  </ListItemButton>
                </NextLink>
              ) : (
                <ListItemButton
                  onClick={() => handleNavClick(item)}
                  sx={{
                    paddingY: 1.5,
                    paddingX: 2,
                    "&:hover": {
                      backgroundColor: "rgba(255, 193, 7, 0.08)",
                    },
                    ...(active && {
                      backgroundColor: "rgba(255, 193, 7, 0.12)",
                      "& .MuiListItemText-primary": {
                        color: "#FFC107",
                        fontWeight: 600,
                      },
                    }),
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "16px",
                      fontWeight: active ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              )}
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ marginTop: 2 }} />

      {/* Social Links */}
      {socialGroup && socialGroup.length > 0 && (
        <Box sx={{ padding: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              marginBottom: 2,
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Theo dõi chúng tôi:
          </Typography>
          <Stack direction="row" spacing={2} sx={{ justifyContent: "center" }}>
            {socialGroup.map((social, index) => (
              <Link
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "#FFC107",
                  color: "#333",
                  textDecoration: "none",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "#FFB300",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(255, 193, 7, 0.3)",
                  },
                }}
              >
                <SalifySocialIcon type={social.type} color="#333" size={24} />
              </Link>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          color: "#333",
          "&:hover": {
            backgroundColor: "rgba(255, 193, 7, 0.08)",
          },
        }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

HaiAnhResponsiveHeaderNav.displayName = "HaiAnhResponsiveHeaderNav";
